/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment} from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import RegistrationFields from '@salesforce/retail-react-app/app/components/forms/registration-fields';
import {AlertIcon} from '@salesforce/retail-react-app/app/components/icons';
import Link from '@salesforce/retail-react-app/app/components/link';
import {Alert, Button, Link as ChakraLink, Stack, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import {noop} from '@salesforce/retail-react-app/app/utils/utils';
import WhySignUp from '@amblique/king-living/app/components/why-sign-up';

const RegisterForm = ({submitForm, clickSignIn = noop, form}) => {
  const styles = useMultiStyleConfig('FormAuth', {});

  return (
    <Fragment>
      <Stack {...styles.header}>
        <Text {...styles.title}>
          <FormattedMessage
            defaultMessage="Sign up or login"
            id="login_page.title"
          />
        </Text>
        <Text {...styles.description}>
          <FormattedMessage
            defaultMessage="Looks like you don’t have an account yet. Let’s set you up"
            id="login_page.register.description"
          />
        </Text>
      </Stack>
      <form
        data-testid="sf-auth-modal-form-register"
        onSubmit={form.handleSubmit(submitForm)}
      >
        <Stack spacing={{base: 6, md: 8}}>
          {form.formState.errors?.global && (
            <Alert status="error">
              <AlertIcon boxSize={4} color="red.500" />
              <Text fontSize="sm" ml={3}>
                {form.formState.errors.global.message}
              </Text>
            </Alert>
          )}
          <RegistrationFields form={form} />
          <Stack spacing={3}>
            <Button
              isLoading={form.formState.isSubmitting}
              type="submit"
              onClick={() => form.clearErrors('global')}
              {...styles.buttonSubmit}
            >
              <FormattedMessage
                defaultMessage="Create Account"
                id="register_form.button.create_account"
              />
            </Button>

            <Stack {...styles.policy}>
              <Text {...styles.policyText}>
                <FormattedMessage
                  defaultMessage="By creating an account, you agree to Salesforce <policy>Privacy Policy</policy> and <terms>Terms & Conditions</terms>"
                  id="register_form.message.agree_to_policy_terms"
                  values={{
                    policy: (chunks) => (
                      <ChakraLink as={Link} to="/privacy-policy" {...styles.policyLink}>
                        {chunks}
                      </ChakraLink>
                    ),

                    terms: (chunks) => (
                      <ChakraLink
                        as={Link}
                        {...styles.policyLink}
                        to="/terms-conditions"
                      >
                        {chunks}
                      </ChakraLink>
                    ),
                  }}
                />
              </Text>
              <Text {...styles.policyText}>
                <FormattedMessage
                  defaultMessage="This site is protected by reCAPTCHA and the Google <policy>Privacy Policy</policy> and <terms>Terms & Conditions</terms>"
                  id="register_form.google.agree_to_policy_terms"
                  values={{
                    policy: (chunks) => (
                      <ChakraLink as={Link} to="/privacy-policy" {...styles.policyLink}>
                        {chunks}
                      </ChakraLink>
                    ),

                    terms: (chunks) => (
                      <ChakraLink
                        as={Link}
                        {...styles.policyLink}
                        to="/terms-conditions"
                      >
                        {chunks}
                      </ChakraLink>
                    ),
                  }}
                />
              </Text>
            </Stack>
          </Stack>
        </Stack>
      </form>
      <WhySignUp />
    </Fragment>
  );
};

RegisterForm.propTypes = {
  submitForm: PropTypes.func,
  clickSignIn: PropTypes.func,
  form: PropTypes.object,
};

export default RegisterForm;
